(ns app.front.view.group.admin.config.role-form
  (:require [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.ui.error-boundary :as eb]
            [app.front.ui.select :as ui-select]
            [app.front.util :refer [for-frag]]
            [app.front.view.group.admin.config-selectors :as conf-sel]
            [app.front.view.group.admin.config.form-schema-subs :as cfs-subs]
            [app.front.view.group.admin.subs :as gas]
            [app.front.view.party.common :as party-common]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub]]
            [core.front.icons :as i]
            [goog.string :as gstr]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn- make-id-to-loc-map
  "Takes a seq of entities and makes a map from their ids to their localizations for dropdowns"
  [entities tr-fn]
  (reduce (fn [acc entity]
            (assoc acc (:db/id entity) (tr-fn entity)))
          {}
          entities))

(rf/reg-sub ::party-role-loc
  :<- [::gas/party-roles]
  (fn [party-roles _]
    (make-id-to-loc-map party-roles party-common/party-role-label)))

(rf/reg-sub ::form-schema-loc
  :<- [::cfs-subs/role-schemas]
  (fn [schemas _]
    (make-id-to-loc-map schemas :form.schema/name)))

(defn- update-config-list [*group-config path item-id k v]
  (swap! *group-config
         update
         path
         (fn [old-list]
           (p/update-first old-list {:db/id item-id} #(assoc % k v)))))


(defn role-select
  "A single role-form select component with dropdowns for role and form schema.
  The :items key under role-opts and form-opts is used to include the select items"
  [tr {:role.config/keys [form-schema party-role] role-config-id :db/id} prefix role-opts form-opts & [children]]
  [:div.flex.items-end.mb-3
   [:div.mr-3
    [ui-select/select
     (merge (dissoc role-opts :items)
            {:id           (str prefix "role-" role-config-id)
             :label-text   (or (tr [:role/role]) "")
             :values       (->> role-opts :items (map :db/id))
             :val->str     (<sub [::party-role-loc])
             :value        (or (:db/id party-role)
                               party-role
                               :none)
             :allow-empty? false})]]
   [:div.mr-3
    [ui-select/select
     (merge (dissoc form-opts :items)
            {:id           (str prefix "form-" role-config-id)
             :label-text   (tr [:entity.type/form])
             :values       (->> form-opts :items (map :db/id))
             :val->str     (<sub [::form-schema-loc])
             :value        form-schema
             :allow-empty? false})]]
   children])

(defn role-form-configurator
  "Component for configuring the forms specific to a given party role in the group-config
  Only one form may be assigned per role and the inherited form roles from up the group-config hierarchy are
  displayed as disabled"
  [{:keys [tr parent-config *group-config] :as editor-ctx}]
  [eb/error-boundary
   (let [parent-role-forms (:role-form-schemas parent-config)
         schemas           (<sub [::cfs-subs/role-schemas])
         all-roles         (<sub [::conf-sel/selectable-party-roles
                                  (conf-sel/get-enabled-roles
                                    :group.config/applicable-party-roles
                                    editor-ctx)])
         enabled-role-ids  (du/entity-id-set all-roles)]
     (when (seq schemas)
       (cc/form-group
         {:legend-text (tr [:group-admin/role-forms])
          :class-name  "role-form-configurator"}
         ;; Inherited ("parent") role forms
         (for-frag [{:role.config/keys [party-role] :as role-config} parent-role-forms
                    :let [party-role (p/find-first all-roles {:db/id party-role})]]
           [role-select tr (assoc role-config :role.config/party-role party-role) "parent-"
            {:disabled true
             :items    [party-role]}
            {:disabled true
             :items    [(p/find-first schemas {:db/id (:role.config/form-schema role-config)})]}])
         ;; Role forms defined (and editable) on the current level of the config tree
         (let [role-configs (->> (:group.config/role-configs @*group-config)
                                 (sort-by #(-> % :db/id str)))
               role-in-use? (->> role-configs (map :role.config/party-role) set)
               avail-roles  (->> all-roles
                                 (remove (comp role-in-use? :db/id))
                                 (sort-by party-common/party-role-label))]
           (for-frag [{:role.config/keys [party-role form-schema] :as role-config} role-configs
                      :let [party-role  (<sub [::entities/entity party-role])
                            update-conf (partial update-config-list
                                                 *group-config
                                                 :group.config/role-configs
                                                 (:db/id role-config))]]
             [:<>
              [role-select tr (assoc role-config :role.config/party-role party-role) ""
               {:on-change #(update-conf :role.config/party-role %)
                :items     (concat [party-role] avail-roles)} ; add current value (can be nil) so it displays correctly
               {:on-change #(update-conf :role.config/form-schema %)
                :items     (concat (when (= form-schema :none) [nil]) ; add unselected as nil
                                   schemas)}
               [:div.pb-4
                (cc/icon {:icon        i/remove
                          :description (tr [:remove])
                          :class-name  "cursor-pointer"
                          :on-click    (fn [_]
                                         (swap! *group-config update
                                                :group.config/role-configs
                                                #(remove (comp #{(:db/id role-config)} :db/id)
                                                         %)))})]]
              (when-not (contains? enabled-role-ids (:db/id party-role))
                [ui/warning (tr [:group-admin/role-not-applicable])])]))
         (cc/button {:kind             :tertiary
                     :size             :field
                     :icon             i/plus
                     :icon-description (tr [:add])
                     :on-click         #(swap! *group-config
                                               update
                                               :group.config/role-configs
                                               concat
                                               [{:db/id                   (gstr/createUniqueString)
                                                 :role.config/form-schema :none}])}
                    (tr [:add])))))])
